import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import {
  productMasterService,
  type ProductMasterListItem,
} from '@/modules/productMaster/productMasterService';

const inputClass =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary';

export type PickedProduct = {
  id: number;
  code: string;
  name: string;
  client_code?: string | null;
};

type Props = {
  label: string;
  value: PickedProduct | null;
  onChange: (product: PickedProduct | null) => void;
  excludeId?: number | null;
  excludeIds?: number[];
};

export function ProductCodePicker({ label, value, onChange, excludeId, excludeIds }: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [results, setResults] = useState<ProductMasterListItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const blocked = new Set(
    [...(excludeIds || []), excludeId].filter((x): x is number => typeof x === 'number')
  );

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open || !debounced) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    productMasterService
      .list({ search: debounced, pageSize: 20 })
      .then((res) => {
        if (cancelled) return;
        setResults((res.results || []).filter((r) => !blocked.has(r.id)).slice(0, 12));
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, open, excludeId, excludeIds]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  if (value) {
    return (
      <div className="block text-sm font-medium text-gray-700">
        {label}
        <div className="mt-1 flex items-start justify-between gap-2 rounded-md border border-stocker-primary/30 bg-stocker-primaryLight/40 px-3 py-2">
          <div className="min-w-0">
            <p className="font-mono text-sm font-semibold text-gray-900">{value.code}</p>
            <p className="truncate text-sm font-normal text-gray-700">{value.name}</p>
            {value.client_code && value.client_code !== value.code ? (
              <p className="text-xs font-normal text-gray-500">
                {t('productMaster.clientCode')}: {value.client_code}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="shrink-0 rounded p-1 text-gray-500 hover:bg-white/80 hover:text-gray-800"
            aria-label={t('equivalences.clearProduct')}
            onClick={() => {
              onChange(null);
              setQuery('');
              setOpen(false);
            }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="relative block text-sm font-medium text-gray-700">
      {label}
      <div className="relative mt-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          className={`${inputClass} pl-9`}
          value={query}
          placeholder={t('equivalences.searchProductPlaceholder')}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          autoComplete="off"
        />
      </div>
      {open && query.trim() ? (
        <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          {loading ? (
            <li className="px-3 py-2 text-sm font-normal text-gray-500">{t('common.loading')}</li>
          ) : results.length === 0 ? (
            <li className="px-3 py-2 text-sm font-normal text-gray-500">
              {t('equivalences.noProductMatch')}
            </li>
          ) : (
            results.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-stocker-primaryLight/40"
                  onClick={() => {
                    onChange({
                      id: row.id,
                      code: row.code,
                      name: row.name,
                      client_code: row.client_code,
                    });
                    setQuery('');
                    setOpen(false);
                  }}
                >
                  <span className="font-mono text-sm font-semibold text-gray-900">{row.code}</span>
                  <span className="truncate text-sm font-normal text-gray-600">{row.name}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
